import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';

interface NavigationProps {
  currentView: string;
  setView: (view: string) => void;
  cartCount: number;
  onCartClick: () => void;
}

// Primary nav — what a shopper needs up top:
// know us (Heritage), buy the line (Shop), get the specs (Specifications).
const PRIMARY_NAV: { id: string; label: string }[] = [
  { id: 'home', label: 'Heritage' },
  { id: 'prototype', label: 'Shop' },
  { id: 'specs', label: 'Specifications' },
];

// Demoted to a Resources dropdown — SEO-indexable, useful for post-application
// onboarding, but no longer competing with the conversion event for nav slots.
const RESOURCES_NAV: { id: string; label: string }[] = [
  { id: 'education', label: 'Care & Safety' },
  { id: 'news', label: 'News' },
];

// Inline WhatsApp brand icon — matches the Footer + floating button so users
// see one consistent glyph everywhere.
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const WHATSAPP_URL =
  "https://wa.me/14166840010?text=" +
  encodeURIComponent("Hi! I'm interested in your machetes.");

const Navigation: React.FC<NavigationProps> = ({ currentView, setView, cartCount, onCartClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const resourcesRef = useRef<HTMLDivElement>(null);

  const handleNav = (view: string) => {
    setView(view);
    setIsMobileMenuOpen(false);
    setIsResourcesOpen(false);
  };

  // Close the Resources dropdown on outside click
  useEffect(() => {
    if (!isResourcesOpen) return;
    const handler = (e: MouseEvent) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setIsResourcesOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [isResourcesOpen]);

  const isResourcesActive = RESOURCES_NAV.some((item) => item.id === currentView);

  return (
    <nav className="sticky top-0 z-50 bg-forge text-paper border-b border-forge-deep/40">
      <div className="max-w-editorial mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">

        {/* Brand */}
        <button
          onClick={() => handleNav('home')}
          className="flex items-center gap-3 group shrink-0"
          aria-label="The Machete Man — Home"
        >
          <img
            src="/images/logo-shield.webp"
            alt=""
            className="w-16 h-16 object-contain opacity-95 group-hover:opacity-100 transition-opacity"
          />
          <div className="flex flex-col justify-center">
            <span className="font-editorial italic text-2xl font-light text-paper-warm tracking-tight leading-none">
              The Machete Man
            </span>
            <span className="hidden lg:block font-mono text-[10px] tracking-[0.18em] uppercase text-ink-300 mt-1">
              Proudly Canadian &middot; Toronto
            </span>
          </div>
        </button>

        {/* Desktop Primary Links */}
        <div className="hidden md:flex items-center gap-7 lg:gap-9">
          {PRIMARY_NAV.map((item) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className={`relative text-sm py-2.5 transition-colors duration-150 ${
                  isActive ? 'text-paper' : 'text-paper/75 hover:text-paper'
                }`}
              >
                {item.label}
                {isActive && (
                  <span className="absolute left-0 -bottom-1 w-full h-px bg-ember" aria-hidden="true" />
                )}
              </button>
            );
          })}

          {/* Resources dropdown */}
          <div ref={resourcesRef} className="relative">
            <button
              onClick={() => setIsResourcesOpen(!isResourcesOpen)}
              className={`relative text-sm transition-colors duration-150 inline-flex items-center gap-1 ${
                isResourcesActive ? 'text-paper' : 'text-paper/75 hover:text-paper'
              }`}
              aria-expanded={isResourcesOpen}
              aria-haspopup="true"
            >
              Resources
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-150 ${
                  isResourcesOpen ? 'rotate-180' : ''
                }`}
                strokeWidth={1.75}
              />
              {isResourcesActive && (
                <span className="absolute left-0 -bottom-1 w-full h-px bg-ember" aria-hidden="true" />
              )}
            </button>
            {isResourcesOpen && (
              <div className="absolute top-full right-0 mt-3 w-48 bg-forge border border-paper/10 shadow-float py-1.5">
                {RESOURCES_NAV.map((item) => {
                  const isActive = currentView === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNav(item.id)}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                        isActive
                          ? 'text-ember bg-paper/5'
                          : 'text-paper/85 hover:text-paper hover:bg-paper/5'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right cluster: WhatsApp · Cart · Apply pill */}
        <div className="hidden md:flex items-center gap-4 lg:gap-5 shrink-0">

          {/* WhatsApp — persistent "Chat to Order" path for diaspora retailers */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-paper/75 hover:text-paper text-sm transition-colors duration-150 group"
            aria-label="WhatsApp enquiry"
          >
            <WhatsAppIcon className="w-4 h-4 fill-paper/75 group-hover:fill-paper transition-colors" />
            <span className="hidden lg:inline font-mono text-[11px] tracking-wider">
              416-684-0010
            </span>
          </a>

          {/* Cart */}
          <button
            onClick={onCartClick}
            className="relative text-paper/75 hover:text-paper transition-colors duration-150 p-2 -m-2"
            aria-label={`Cart (${cartCount} items)`}
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-ember rounded-full text-[10px] flex items-center justify-center font-medium text-paper-warm">
                {cartCount}
              </span>
            )}
          </button>

          {/* Wholesale link — secondary path for trade buyers (retail shoppers use Shop + Cart) */}
          <button
            onClick={() => handleNav('wholesale')}
            className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 border transition-colors duration-150 ${
              currentView === 'wholesale'
                ? 'border-ember bg-ember text-paper-warm'
                : 'border-paper/30 text-paper hover:border-paper hover:bg-paper/5'
            }`}
          >
            Wholesale
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-paper p-1"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-forge border-t border-forge-deep/40 absolute w-full left-0 z-50">
          <div className="max-w-editorial mx-auto px-6 py-6 space-y-1">
            {/* Primary items */}
            {PRIMARY_NAV.map((item) => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`block w-full text-left py-3 text-base border-b border-paper/10 transition-colors duration-150 ${
                    isActive ? 'text-ember' : 'text-paper/85 hover:text-paper'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}

            {/* Resources sub-items (mobile shows them flat — no dropdown UI on tap targets) */}
            {RESOURCES_NAV.map((item) => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`block w-full text-left py-3 text-base border-b border-paper/10 transition-colors duration-150 ${
                    isActive ? 'text-ember' : 'text-paper/85 hover:text-paper'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}

            {/* Cart */}
            <button
              onClick={() => {
                onCartClick();
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 w-full text-left py-3 text-base border-b border-paper/10 text-paper/85 hover:text-paper transition-colors duration-150"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              <span>Cart</span>
              {cartCount > 0 && (
                <span className="ml-auto min-w-[20px] h-5 px-2 bg-ember rounded-full text-xs flex items-center justify-center font-medium text-paper-warm">
                  {cartCount}
                </span>
              )}
            </button>

            {/* WhatsApp */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 w-full text-left py-3 text-base border-b border-paper/10 text-paper/85 hover:text-paper transition-colors duration-150"
            >
              <WhatsAppIcon className="w-5 h-5 fill-paper/85" />
              <span>WhatsApp · (416) 684-0010</span>
            </a>

            {/* Wholesale — secondary path for trade buyers */}
            <button
              onClick={() => handleNav('wholesale')}
              className="block w-full text-center mt-5 py-3.5 text-base font-medium bg-ember hover:bg-ember-deep text-paper-warm transition-colors duration-150"
            >
              Wholesale &amp; trade
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
